import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';

import { AuthService } from '@auth0/auth0-angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { SuiBreakpointService, SuiModalService, SuiToastService } from '@semeris/ui';
import type { SuiModalRef, ToastAction } from '@semeris/ui';
import { isEqual } from 'lodash-es';
import { combineLatest, EMPTY, from, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { routeSegments } from '../../features/shared/constants/workspace-scoped-routes';
import { writeTabWorkspace, type TabWorkspace } from '../../features/shared/utils/tab-workspace-storage';
import { wsLink } from '../../features/shared/utils/workspace-link';
import { RootState } from '../root-state.model';
import { AppActions, EditTemplateModalId } from './app.actions';
import { AppSelectors } from './app.selectors';
import { BuiltInChecklistService } from '../../features/checklist/services/built-in-checklist.service';
import type { DependencyTreeContainerComponent } from '../../features/dependency/containers/dependency-tree-container/dependency-tree-container.component';
import type {
  FeedbackModalComponent,
  FeedbackModalResult,
} from '../../features/feedback/components/feedback-modal/feedback-modal.component';
import { FeedbackService } from '../../features/feedback/service/feedback.service';
import { mergeWorkspaces } from '../../features/home/models/merge-workspaces';
import { ActivityService } from '../../features/home/services/activity.service';
import { ApplicationService } from '../../features/home/services/application.service';
import type { EditTemplateModalComponent } from '../../features/market-comparison/components/edit-template-modal/edit-template-modal.component';
import { EditTemplateModalConfig } from '../../features/market-comparison/models/template-modal.models';
import { workspaceRepickUrl } from '../../features/setup/workspace-repick';
import {
  FAIL_NOTIFICATION_CONFIGURATION,
  NETWORK_DOWN_NOTIFICATION_KEY,
  NotificationConfig,
  SUCCESS_NOTIFICATION_CONFIGURATION,
  WARNING_NOTIFICATION_CONFIGURATION,
} from '../../features/shared/constants/notification-configurations';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { UserTemplateEntityBase } from '../../features/shared/models/common/user-template-entity-base.model';
import { WebSocketEntities, WebSocketReloadEvent } from '../../features/shared/models/web-socket/web-socket.models';
import { DownloadService } from '../../features/shared/services/download.service';
import { UpdateService } from '../../features/shared/services/update.service';
import { createPersonalWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import { createNotificationEffect, PushNotificationRecord } from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { SystemMessageModalService } from '../../features/system-message/services/system-message-modal.service';
import { SystemMessageService } from '../../features/system-message/services/system-message.service';
import { TermSynonymOrganizerService } from '../../features/term-synonym-organizer/services/term-synonym-organizer.service';
import { WorkspaceRecordService } from '../../features/workspace-ui/workspace-record.service';
import { ManagerActions } from '../manager/manager.actions';
import { TopicListActions } from '../topic-list/topic-list.actions';
import { TransactionActions } from '../transaction/transaction.actions';
import { UserProfileSelectors } from '../user-profile/user-profile.selectors';

/** Pages an account without access is confined to. */
const STATUS_PAGE_SEGMENTS: readonly string[] = [SemerisRoutes.welcome, SemerisRoutes.blocked];

@Injectable()
export class AppEffects {
  private readonly breakpoint$ = toObservable(inject(SuiBreakpointService).current);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly appService = inject(ApplicationService);
  private readonly workspaceRecords = inject(WorkspaceRecordService);
  private readonly toast = inject(SuiToastService);
  private readonly downloadService = inject(DownloadService);
  private readonly activityService = inject(ActivityService);
  private readonly authService = inject(AuthService);
  private readonly suiModalService = inject(SuiModalService);
  private readonly webSocketService = inject(WebSocketService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly updateService = inject(UpdateService);
  private readonly synonymService = inject(TermSynonymOrganizerService);
  private readonly builtInChecklistService = inject(BuiltInChecklistService);
  private readonly systemMessageService = inject(SystemMessageService);
  private readonly systemMessageModalService = inject(SystemMessageModalService);

  /**
   * Whether this session needs asset-scoped boot data — false only for new-style workspaces,
   * which have no asset classes and so get empty transactions / managers / topics / synonyms.
   *
   * The resolved workspace wins when it exists: `SetWorkspaceSuccess` updates the app store but
   * NOT the profile, so after an in-app switch the profile's flag is stale. On cold boot the
   * workspace list is empty and the pre-fetched profile is the only source.
   */
  private readonly needsAssetScopedData$: Observable<boolean> = combineLatest([
    this.store.select(AppSelectors.selectedWorkspace),
    this.store.select(UserProfileSelectors.profile).pipe(filter((profile) => !!profile)),
  ]).pipe(
    take(1),
    map(([workspace, profile]) => (workspace ? workspace.legacy : profile.selectedWorkspaceLegacy) !== false),
  );

  private readonly notifications: PushNotificationRecord = {
    [AppActions.SendFeedbackSuccess.type]: { message: 'Feedback sent', isSuccess: true },
    [AppActions.SendFeedbackFail.type]: {
      message: 'Failed to send the feedback',
      isSuccess: false,
    },
  };

  /**
   * Current open template-modal ref, as a signal. The `closed$ → tap`
   * subscription has to guard `this.templateModalRef() === ref` before
   * clearing, because the previous modal's deferred close emission can
   * race the assignment of a freshly-opened modal (open → assign → late
   * old-tap fires → clears the NEW ref). The guard makes that case a
   * no-op.
   */
  private templateModalRef = signal<SuiModalRef | null>(null);

  /**
   * Mirrors this tab's scope into `sessionStorage`, so a reload re-enters the same workspace and
   * `pre-fetch.js` can claim it before Angular boots.
   *
   * Derived from the store rather than from a list of actions — anything that changes the scope is
   * covered without having to remember to add it here. Only a RESOLVED scope is written: the
   * profile's stored selection can name assets for a different workspace than this tab claims,
   * and caching that hands the next boot's `pre-fetch.js` the wrong scope.
   */
  persistTabWorkspace$: Observable<unknown> = createEffect(
    () =>
      this.store.select(AppSelectors.resolvedWorkspaceScope).pipe(
        filter((scope): scope is TabWorkspace => !!scope),
        distinctUntilChanged((prev, next) => prev.workspaceId === next.workspaceId && isEqual(prev.assets, next.assets)),
        tap((scope) => writeTabWorkspace(scope)),
      ),
    { dispatch: false },
  );

  exportAsJson$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.ExportAsJson),
        map((action) => {
          this.downloadService.exportAsJson(action.value, action.filename);
        }),
      ),
    { dispatch: false },
  );

  logout$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.Logout),
        switchMap((action) =>
          this.activityService.logoutActivity().pipe(
            catchError((err) => {
              console.error(err);
              return of({});
            }),
            tap(() => {
              localStorage.removeItem('semeris-user-email');
              localStorage.removeItem('semeris-user-display-name');
              this.authService
                .logout({
                  logoutParams: {
                    returnTo: `${window.origin}/auth/logout`,
                  },
                })
                .subscribe();
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  addActivity$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.AddActivity),
        switchMap((action) => this.activityService.addActivity(action.activity, action.meta)),
      ),
    { dispatch: false },
  );

  openFeedback$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.OpenFeedback),
      withLatestFrom(this.authService.user$),
      switchMap(([action, user]) =>
        from(import('../../features/feedback/components/feedback-modal/feedback-modal.component')).pipe(
          switchMap((m) => {
            const modal = this.suiModalService.open<FeedbackModalComponent, FeedbackModalResult>(m.FeedbackModalComponent, {
              width: 'lg',
              data: {
                email: user.email,
                subject: action.subject,
                message: action.message,
              },
            });

            return modal.closed$.pipe(
              filter((feedback): feedback is FeedbackModalResult => !!feedback),
              map((feedback) => AppActions.SendFeedback({ feedback })),
            );
          }),
          catchError((error) => {
            console.error('Failed to load feedback modal', error);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  sendFeedback$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.SendFeedback),
      switchMap((action) => {
        return this.feedbackService.sendMail(action.feedback).pipe(
          map(() => AppActions.SendFeedbackSuccess()),
          reportEffectError(AppActions.SendFeedbackFail),
        );
      }),
    ),
  );

  /**
   * Re-merges the gateway's workspaces into `availableWorkspaces` after a create/edit. The legacy
   * presets aren't re-read — they come from init data and only the gateway side can have changed.
   */
  refreshWorkspaces$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.RefreshWorkspaces),
      withLatestFrom(this.store.select(AppSelectors.availableWorkspaces), this.store.select(AppSelectors.assetWorkspaceIds)),
      switchMap(([action, current, assetWorkspaceIds]) =>
        this.workspaceRecords.refresh().pipe(
          map((gateway) =>
            AppActions.RefreshWorkspacesSuccess({
              workspaces: mergeWorkspaces(
                current.filter((workspace) => workspace.legacy !== false),
                gateway,
                assetWorkspaceIds,
              ),
            }),
          ),
          catchError((error: unknown) => {
            console.error('[AppEffects] workspace refresh failed', error);
            if (action.silent) {
              return EMPTY;
            }
            // The write itself succeeded, so say what's stale rather than implying it was lost.
            return of(
              AppActions.PushNotification({
                message: 'Your change was saved, but the workspace list could not be refreshed. Reload to see it.',
                config: 'fail',
              }),
            );
          }),
        ),
      ),
    ),
  );

  /** Mirrors `AssetGuard`'s check, for a workspace that disappears from the list mid-session. */
  revalidateWorkspace$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.RefreshWorkspacesSuccess),
        withLatestFrom(this.store.select(AppSelectors.selectedWorkspaceIsUnresolved), this.store.select(UserProfileSelectors.profile)),
        filter(([, isUnresolved, profile]) => isUnresolved && !profile?.impersonatedTenant),
        tap(() => {
          void this.router.navigateByUrl(workspaceRepickUrl(this.router));
        }),
      ),
    { dispatch: false },
  );

  startObserv$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.StartObservLayout),
      exhaustMap(() => this.breakpoint$),
      takeUntil(this.actions$.pipe(ofType(AppActions.StopObservLayout))),
      map((breakpoint) => AppActions.LayoutChanged({ breakpoint })),
    ),
  );

  loadInitData$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitData),
      tap(() => {
        console.debug(`[AppActions.LoadInitData] received`);
      }),
      switchMap(() => {
        const start = Date.now();
        console.debug(`[AppActions.LoadInitData] starting getInitialData`);
        return this.appService.getInitialData().pipe(
          tap(() => {
            console.debug(`[AppActions.LoadInitData] getInitialData done in ${Date.now() - start} ms`);
          }),
          map((data) => {
            console.debug(`[AppActions.LoadInitData] dispatching LoadInitDataSuccess`);
            return AppActions.LoadInitDataSuccess({ data });
          }),
        );
      }),
    ),
  );

  loadBuiltInChecklists$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitDataSuccess, AppActions.LoadBuiltInChecklists),
      switchMap(() => this.needsAssetScopedData$),
      switchMap((needsAssetScopedData) => {
        // Built-in checklists are keyed by asset class — a new-style workspace has none, and the
        // request would 403 on its empty asset scope before returning the same empty map.
        if (!needsAssetScopedData) {
          return of(AppActions.LoadBuiltInChecklistsSuccess({ topicIdsByAsset: {} }));
        }
        return this.builtInChecklistService.getResolved().pipe(
          map((resolved) =>
            AppActions.LoadBuiltInChecklistsSuccess({
              topicIdsByAsset: resolved.reduce<Record<string, string[]>>(
                (acc, pair) => ({ ...acc, [pair.assetShortName]: pair.topicIds }),
                {},
              ),
            }),
          ),
          catchError((error) => {
            console.error('[AppEffects] failed to load built-in checklists', error);
            return of(AppActions.LoadBuiltInChecklistsSuccess({ topicIdsByAsset: {} }));
          }),
        );
      }),
    ),
  );

  loadSynonymClasses$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitDataSuccess, AppActions.LoadSynonymClasses),
      switchMap(() => this.needsAssetScopedData$),
      switchMap((needsAssetScopedData) => {
        // Synonym classes are keyed by asset class — a new-style workspace has none.
        if (!needsAssetScopedData) {
          return of(AppActions.LoadSynonymClassesSuccess({ synonymClassesByAsset: {} }));
        }
        return this.synonymService.getAllSynonymClasses().pipe(
          map((items) => {
            const synonymClassesByAsset: Record<string, string[][]> = {};
            for (const item of items) {
              synonymClassesByAsset[item.assetShortName] = item.synonymClasses;
            }
            return AppActions.LoadSynonymClassesSuccess({ synonymClassesByAsset });
          }),
          catchError(() => of(AppActions.LoadSynonymClassesSuccess({ synonymClassesByAsset: {} }))),
        );
      }),
    ),
  );

  /**
   * Deliberately its own effect rather than a leg of `getInitialData()`'s forkJoin — that
   * forkJoin gates splash removal, so a slow announcement fetch would delay app boot.
   * A failure here must be invisible to the user.
   */
  loadSystemMessages$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.LoadInitDataSuccess, AppActions.LoadSystemMessages),
      switchMap(() =>
        this.systemMessageService.getActive().pipe(
          map((systemMessages) => AppActions.LoadSystemMessagesSuccess({ systemMessages })),
          catchError((error) => {
            console.error('[AppEffects] failed to load system messages', error);
            return of(AppActions.LoadSystemMessagesSuccess({ systemMessages: [] }));
          }),
        ),
      ),
    ),
  );

  /** `exhaustMap`: a workspace switch reloads the messages, which must not reopen a modal that is still showing. */
  showSystemMessageModals$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.LoadSystemMessagesSuccess),
        exhaustMap(({ systemMessages }) =>
          this.systemMessageModalService.showPending(systemMessages).pipe(
            catchError((error) => {
              console.error('[AppEffects] failed to show system message modals', error);
              return EMPTY;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  pushNotificationEffect$: Observable<unknown> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.PushNotification),
        tap((action) => {
          const config: NotificationConfig =
            action.config === 'success'
              ? SUCCESS_NOTIFICATION_CONFIGURATION
              : action.config === 'fail'
                ? FAIL_NOTIFICATION_CONFIGURATION
                : (action.config ?? {});

          // Action-button bridge: a labelled toast action dispatches the same
          // PushNotificationAction the snack-bar action used to, so the three
          // downstream handlers (reload_application / reload_token / *Reload) are untouched.
          const actions: ToastAction[] | undefined = action.action
            ? [
                {
                  label: action.action,
                  onClick: () => this.store.dispatch(AppActions.PushNotificationAction({ id: action.id ?? '' })),
                },
              ]
            : undefined;

          this.toast.show({
            message: action.message,
            color: config.color,
            duration: config.duration,
            dismissible: action.closable,
            actions,
            key: config.key ?? action.message,
            position: config.position,
          });
        }),
      ),
    { dispatch: false },
  );

  pushNotificationActionEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.PushNotificationAction),
        filter((action) => action.id === 'reload_application'),
        map(() => (window.location.href = '/home')),
      ),
    { dispatch: false },
  );

  reloadTokenActionEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.PushNotificationAction),
      filter((action) => action.id === 'reload_token'),
      map((action) => AppActions.GetNewTokenAndReload()),
    ),
  );

  getNewTokenAndReloadEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.GetNewTokenAndReload),
        switchMap(() =>
          this.authService.getAccessTokenSilently({ cacheMode: 'off' }).pipe(
            // A failed silent refresh must NOT swallow the reload — impersonation is resolved
            // server-side from the user record, so the boot after reload picks it up regardless.
            catchError(() => of(null)),
          ),
        ),
        tap(() => window.location.reload()),
      ),
    { dispatch: false },
  );

  clearPushNotificationEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.ClearPushNotification),
        tap(() => {
          this.toast.dismissAll();
        }),
      ),
    { dispatch: false },
  );

  enterOfflineModeEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.EnterOfflineMode),
      withLatestFrom(this.store),
      filter(([action, store]) => !store.app.isOffline),
      map(() => AppActions.EnterOfflineModeSuccess()),
    ),
  );

  enterOfflineModeSuccessEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.EnterOfflineModeSuccess),
      map(() =>
        AppActions.PushNotification({
          message: 'Your network appears to be down. Please try again shortly.',
          action: 'Reload application',
          id: 'reload_application',
          closable: true,
          config: {
            color: 'error',
            duration: 0,
            position: 'top-center',
            key: NETWORK_DOWN_NOTIFICATION_KEY,
          },
        }),
      ),
    ),
  );

  restoreFromOfflineModeEffect$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.RestoreFromOfflineMode),
      map(() =>
        AppActions.PushNotification({
          message: 'Your network appears to be online again.',
          config: {
            color: 'success',
            duration: 3_000,
            position: 'top-center',
          },
        }),
      ),
    ),
  );

  showInfoModalEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.ShowInfoModal),
        switchMap((action) =>
          from(import('../../features/shared/components/info-modal/info-modal.component')).pipe(
            map((m) => this.suiModalService.open(m.InfoModalComponent, { width: 'sm', data: action.data })),
            catchError((error) => {
              console.error('Failed to load info modal', error);
              return EMPTY;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  openTemplateModalEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.OpenTemplateModal),
      withLatestFrom(this.store.select(AppSelectors.assetOptions)),
      tap(() => {
        this.closeTemplateModal();
      }),
      switchMap(([action, assetOptions]) =>
        from(import('../../features/market-comparison/components/edit-template-modal/edit-template-modal.component')).pipe(
          switchMap((m) => {
            const ref = this.suiModalService.open<EditTemplateModalComponent, Partial<UserTemplateEntityBase>>(
              m.EditTemplateModalComponent,
              {
                width: 'xl',
                height: 'lg',
                data: {
                  ...action.data,
                  assets: assetOptions,
                  workspaceTargeting:
                    action.modalId === EditTemplateModalId.EditTopic || action.modalId === EditTemplateModalId.EditTopicList,
                } as EditTemplateModalConfig,
              },
            );
            this.templateModalRef.set(ref);
            return ref.closed$.pipe(
              take(1),
              tap(() => {
                // Race guard: only clear if this tap belongs to the currently-
                // tracked modal. A previous modal's deferred close emission
                // can land AFTER we've already assigned a new ref above.
                if (this.templateModalRef() === ref) {
                  this.templateModalRef.set(null);
                }
              }),
              filter((data): data is Partial<UserTemplateEntityBase> => !!data),
              map((data) =>
                AppActions.TemplateModalClosed({
                  data,
                  modalId: action.modalId,
                  additionalInfo: action.additionalInfo,
                }),
              ),
            );
          }),
          catchError((error) => {
            console.error('Failed to load edit-template modal', error);
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  openDependencyModalEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.OpenDependencyModal),
        switchMap((action) =>
          from(import('../../features/dependency/containers/dependency-tree-container/dependency-tree-container.component')).pipe(
            map((m) =>
              this.suiModalService.open<DependencyTreeContainerComponent>(m.DependencyTreeContainerComponent, {
                data: {
                  id: action.data.id,
                  type: action.data.type,
                },
                width: 'full',
                height: 'full',
              }),
            ),
            catchError((error) => {
              console.error('Failed to load dependency tree modal', error);
              return EMPTY;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  loadProfileSuccessLoadInitDataEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TriggerAppInit),
      tap(() => {
        console.debug(`[AppActions.TriggerAppInit] received`);
      }),
      mergeMap(() => {
        console.debug(`[AppActions.TriggerAppInit] dispatching [AppActions.LoadInitData]`);
        return [AppActions.LoadInitData()];
      }),
    ),
  );

  /** Transaction, manager and topic loads — parallel to LoadInitData, none gates the boot shell. */
  loadAssetScopedBootDataEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TriggerAppInit),
      switchMap(() => this.needsAssetScopedData$),
      mergeMap((needsAssetScopedData) => {
        if (!needsAssetScopedData) {
          // Settle transactions rather than skipping: `loadSettled` gates DealRedirectToGuard,
          // and leaving it unset hangs any deal route instead of resolving it as "absent".
          console.debug(`[AppActions.TriggerAppInit] new-style workspace — settling transactions empty, skipping manager/topic loads`);
          return [TransactionActions.LoadTransactionsSuccess({ transactions: [] })];
        }
        console.debug(
          `[AppActions.TriggerAppInit] dispatching [TransactionActions.LoadTransactions] + [ManagerActions.LoadList] + [TopicListActions.LoadTopics]`,
        );
        return [TransactionActions.LoadTransactions({}), ManagerActions.LoadList(), TopicListActions.LoadTopics()];
      }),
    ),
  );

  wsEffect$: Observable<Action> = createPersonalWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.App,
    () => {
      if (STATUS_PAGE_SEGMENTS.includes(routeSegments(this.router.url)[0])) {
        return AppActions.GetNewTokenAndReload();
      }

      return AppActions.PushNotification({
        message: `Your permissions have changed. Reload required.`,
        action: `Reload`,
        closable: false,
        config: { ...WARNING_NOTIFICATION_CONFIGURATION, duration: 0 },
        id: 'reload_token',
      });
    },
    true,
  );

  wsResetEffect$: Observable<Action> = createPersonalWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Reset,
    () => {
      this.updateService.hardReset();
      return AppActions.PushNotification({
        message: `The application has been reset due to breaking changes.`,
        config: 'success',
      });
    },
    true,
  );

  closeTemplateModalEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.PushNotificationAction),
        filter((action) =>
          [WebSocketReloadEvent.DatapointReload, WebSocketReloadEvent.MarketCompareReload, WebSocketReloadEvent.FormulaReload].includes(
            action.id as WebSocketReloadEvent,
          ),
        ),
        map((action) => {
          this.closeTemplateModal();
        }),
      ),
    { dispatch: false },
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(this.actions$, this.notifications);

  private closeTemplateModal() {
    const ref = this.templateModalRef();
    if (ref) {
      ref.close();
      this.templateModalRef.set(null);
    }
  }

  redirectOnUnauthorized$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AppActions.Unauthorized),
        // Already there: a second failure would nest the page's own URL into `from`.
        filter(() => routeSegments(this.router.url)[0] !== SemerisRoutes.unauthorized),
        tap(() =>
          this.router.navigate(wsLink(SemerisRoutes.unauthorized), {
            queryParams: { from: window.location.href.slice(window.location.origin.length) },
          }),
        ),
      ),
    { dispatch: false },
  );
}
